import {
  XERO,
  XERO_INVOICE_CSV_COLUMNS,
  createEmptyXeroInvoiceRow
} from './xeroInvoiceCsvColumns'

import {
  PORKER_PRODUCT_CODES,
  PORKER_PRODUCT_DESCRIPTIONS
} from './xeroProductCodes'

import { XERO_INVOICE_DEFAULTS } from './xeroInvoiceDefaults'
import { getXeroAccountCodeByProductCode } from './xeroAccountCodeMapper'
import {
  formatDateWithAddedDays,
  parseShortAustralianDate
} from '../DateHelpers'
import { formatDate, formatDocketDate } from '@/helpers/stringHelpers'

function isPorkerDocket(docket) {
  return Boolean(docket?.job_description?.total_pigs)
}

function isCartonDocket(docket) {
  return Boolean(docket?.job_description?.total_cartons)
}

function normaliseProductType(productType) {
  return String(productType || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
}

function getCustomerName(docket) {
  return docket?.destination?.name || ''
}

function getInvoiceNumber(docket) {
  return docket?.invoice || ''
}

function getDocketNumber(docket) {
  return docket?.docket || ''
}

function getInvoiceDate(docket) {
  const date = docket?.job_description?.loaded_date || docket?.date

  return date ? formatDocketDate(date) : ''
}

function getDueDate(docket) {
  const invoiceDate = getInvoiceDate(docket)
  const parsedDate = parseShortAustralianDate(invoiceDate)

  if (!parsedDate) return ''

  const date = formatDateWithAddedDays(
    parsedDate,
    XERO_INVOICE_DEFAULTS.dueDateOffsetDays
  )

  return formatDate(date)
}

function getPorkerProductCode(item) {
  if (item?.productCode) return item.productCode

  const productType = normaliseProductType(item?.producttype)

  return PORKER_PRODUCT_CODES[productType] || ''
}

function getPorkerDescription(item) {
  const productType = normaliseProductType(item?.producttype)

  const productDescription =
    PORKER_PRODUCT_DESCRIPTIONS[productType] || item?.producttype || 'Porker'

  if (item?.body_no) {
    return `${productDescription} - Body ${item.body_no}`
  }

  return productDescription
}

function getCartonProductCode(item) {
  return item?.productCode || item?.productcode || ''
}

function getCartonDescription(item) {
  return item?.productname || ''
}

const getQuantity = (item) => {
  return Number(item.weight || 0)
}

const getUnitAmount = (item) => {
  return Number(item.price_kg || 0)
}

function getAccountCode(productCode) {
  return getXeroAccountCodeByProductCode(productCode)
}

function getBaseXeroRow(docket) {
  const row = createEmptyXeroInvoiceRow()

  row[XERO.CONTACT_NAME] = getCustomerName(docket)

  row[XERO.INVOICE_NUMBER] = getInvoiceNumber(docket)
  row[XERO.REFERENCE] = getDocketNumber(docket)
  row[XERO.INVOICE_DATE] = getInvoiceDate(docket)
  row[XERO.DUE_DATE] = getDueDate(docket)

  row[XERO.TAX_TYPE] = XERO_INVOICE_DEFAULTS.taxType
  row[XERO.CURRENCY] = XERO_INVOICE_DEFAULTS.currency

  return row
}

function mapPorkerItemToXeroRow(docket, item) {
  const productCode = getPorkerProductCode(item)

  const row = getBaseXeroRow(docket)

  row[XERO.INVENTORY_ITEM_CODE] = productCode
  row[XERO.DESCRIPTION] = getPorkerDescription(item)
  row[XERO.QUANTITY] = getQuantity(item)
  row[XERO.UNIT_AMOUNT] = getUnitAmount(item)
  row[XERO.ACCOUNT_CODE] = getAccountCode(productCode)

  return row
}

function mapCartonItemToXeroRow(docket, item) {
  const productCode = getCartonProductCode(item)

  const row = getBaseXeroRow(docket)

  row[XERO.INVENTORY_ITEM_CODE] = productCode
  row[XERO.DESCRIPTION] = getCartonDescription(item)
  row[XERO.QUANTITY] = getQuantity(item)
  row[XERO.UNIT_AMOUNT] = getUnitAmount(item)
  row[XERO.ACCOUNT_CODE] = getAccountCode(productCode)

  return row
}

export function mapDocketToXeroInvoiceRows(docket) {
  const items = docket?.loaded_items || []

  if (isPorkerDocket(docket)) {
    return items.map((item) => mapPorkerItemToXeroRow(docket, item))
  }

  if (isCartonDocket(docket)) {
    return items.map((item) => mapCartonItemToXeroRow(docket, item))
  }

  return []
}

export function mapDocketsToXeroInvoiceRows(dockets) {
  return dockets.flatMap((docket) => mapDocketToXeroInvoiceRows(docket))
}

export function validateDocketsForXeroExport(dockets) {
  const errors = []

  if (!Array.isArray(dockets) || !dockets.length) {
    return [
      {
        docket: null,
        item: null,
        message: 'No dockets selected for Xero export.'
      }
    ]
  }

  dockets.forEach((docket) => {
    const docketNo = getDocketNumber(docket) || docket?.id || 'Unknown docket'

    if (!getCustomerName(docket)) {
      errors.push({
        docket,
        item: null,
        message: `Docket ${docketNo} is missing customer name.`
      })
    }

    if (!getInvoiceNumber(docket)) {
      errors.push({
        docket,
        item: null,
        message: `Docket ${docketNo} is missing invoice number.`
      })
    }

    if (!getInvoiceDate(docket)) {
      errors.push({
        docket,
        item: null,
        message: `Docket ${docketNo} is missing or has invalid invoice date.`
      })
    }

    if (!Array.isArray(docket?.loaded_items) || !docket.loaded_items.length) {
      errors.push({
        docket,
        item: null,
        message: `Docket ${docketNo} has no loaded items.`
      })

      return
    }

    if (!isPorkerDocket(docket) && !isCartonDocket(docket)) {
      errors.push({
        docket,
        item: null,
        message: `Docket ${docketNo} is not recognised as porker or carton docket.`
      })
    }

    docket.loaded_items.forEach((item) => {
      let productCode = ''

      if (isPorkerDocket(docket)) {
        productCode = getPorkerProductCode(item)

        if (!productCode) {
          errors.push({
            docket,
            item,
            message: `Docket ${docketNo} has porker item without mapped Xero product code.`
          })
        }

        if (!getPorkerDescription(item)) {
          errors.push({
            docket,
            item,
            message: `Docket ${docketNo} has porker item without description.`
          })
        }

        if (getQuantity(item) <= 0) {
          errors.push({
            docket,
            item,
            message: `Docket ${docketNo} has porker item with invalid weight.`
          })
        }

        if (getUnitAmount(item) <= 0) {
          errors.push({
            docket,
            item,
            message: `Docket ${docketNo} has porker item without valid price per kg.`
          })
        }

        if (!item?.body_no) {
          errors.push({
            docket,
            item,
            message: `Docket ${docketNo} has porker item without body number.`
          })
        }
      }

      if (isCartonDocket(docket)) {
        productCode = getCartonProductCode(item)

        if (!productCode) {
          errors.push({
            docket,
            item,
            message: `Docket ${docketNo} has carton item without product code.`
          })
        }

        if (!getCartonDescription(item)) {
          errors.push({
            docket,
            item,
            message: `Docket ${docketNo} has carton item without product name.`
          })
        }

        if (getQuantity(item) <= 0) {
          errors.push({
            docket,
            item,
            message: `Docket ${docketNo} has carton item with invalid weight.`
          })
        }
      }

      if (getUnitAmount(item) <= 0) {
        errors.push({
          docket,
          item,
          message: `Docket ${docketNo} has carton item without valid price per kg.`
        })
      }

      if (productCode && !getAccountCode(productCode)) {
        errors.push({
          docket,
          item,
          message: `Docket ${docketNo} has product code ${productCode} without mapped Xero account code.`
        })
      }
    })
  })

  return errors
}

export { XERO_INVOICE_CSV_COLUMNS }
